import * as React from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { Loader } from 'Src/components/Loader/Loader';
import { HelpModal } from 'Src/components/HelpModal/HelpModal';
import { registerPageHelp } from 'Src/helpers/pageHelp/pageHelp';
import {
  getClubProgrammeBookings,
  selectClubProgrammeBooking,
  unselectClubProgrammeBooking,
  bulkSelectClubProgrammeBookingsForClub,
  bulkSelectClubProgrammeBookingsForDay,
  bulkSelectClubProgrammeBookingsForProgramme,
  removeClubProgrammeBooking,
  confirmClubProgrammeBookingsForClub,
  confirmClubProgrammeBookingsForDay,
  confirmClubProgrammeBookingsForProgramme,
  getClubProgrammeActivationPreview,
  batchActivateClubProgrammeClubs,
  ClubProgrammeBookingsResponse,
  ClubProgrammeBookingDay,
  ClubProgrammeBookingClub,
  ClubProgrammeBookingItem,
  ClubProgrammeBookingConflict,
  ClubProgrammeStudentConflict,
  ClubProgrammeActivationPreviewResponse
} from 'Src/helpers/service/admin/clubProgrammes';
import { BulkActionMenu } from './components/BulkActionMenu';
import {
  weekdayOrder,
  normDay,
  getPrimaryActionForItem,
  getSecondaryActionForItem,
  getActionButtonText
} from './helpers/clubProgrammeBookingsBoard';
import { ClubProgrammeBookingAnswersModal } from './components/ClubProgrammeBookingAnswersModal';
import { ClubProgrammeClubListsModal } from './components/ClubProgrammeClubListsModal';
import { ClubProgrammeBookingConflictModal } from './components/ClubProgrammeBookingConflictModal';
import {
  ClubProgrammeConfirmConflictsModal,
  ConfirmConflictItem
} from './components/ClubProgrammeConfirmConflictsModal';
import { ClubProgrammeActivationPreviewModal } from './components/ClubProgrammeActivationPreviewModal';
import { ClubProgrammeBookingClubColumn } from './components/ClubProgrammeBookingClubColumn';

type Props = {
  user: AppUser;
  programme: any;
  activationRequestToken?: number;
  onActivationAvailabilityChange?: (canOpen: boolean) => void;
  onSelectionModeChange?: (selectionMode: string) => void;
};

type AnswerModalState = { isOpen: false } | { isOpen: true; title: string; item: ClubProgrammeBookingItem };

type ConflictModalState = { isOpen: false } | { isOpen: true; conflict: ClubProgrammeStudentConflict };

type ClubListsModalState =
  | { isOpen: false }
  | {
      isOpen: true;
      clubId: string;
      clubName: string;
    };

type ConfirmConflictsModalState =
  | { isOpen: false }
  | {
      isOpen: true;
      mode: 'club';
      clubId: string;
      conflicts: ConfirmConflictItem[];
      isSubmitting: boolean;
    }
  | {
      isOpen: true;
      mode: 'day';
      selectionDayKey: string;
      conflicts: ConfirmConflictItem[];
      isSubmitting: boolean;
    }
  | {
      isOpen: true;
      mode: 'programme';
      conflicts: ConfirmConflictItem[];
      isSubmitting: boolean;
    };

type ActivationPreviewModalState =
  | { isOpen: false }
  | {
      isOpen: true;
      isLoading: boolean;
      isActivating: boolean;
      data: ClubProgrammeActivationPreviewResponse | null;
    };

export const ClubProgrammeBookingsBoard: React.FC<Props> = ({
  user,
  programme,
  activationRequestToken,
  onActivationAvailabilityChange,
  onSelectionModeChange
}) => {
  const programmeId = String((programme as any)?.id || (programme as any)?._id || '');

  const [isLoading, setIsLoading] = React.useState(true);
  const [isReloading, setIsReloading] = React.useState(false);

  const [data, setData] = React.useState<ClubProgrammeBookingsResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const [includeDrafts, setIncludeDrafts] = React.useState(false);
  const [includeWaitingList, setIncludeWaitingList] = React.useState(true);

  const [activeDay, setActiveDay] = React.useState<string>('MON');

  const [answerModal, setAnswerModal] = React.useState<AnswerModalState>({ isOpen: false });
  const [conflictModal, setConflictModal] = React.useState<ConflictModalState>({ isOpen: false });

  const [actionKey, setActionKey] = React.useState<string | null>(null);

  const [clubListsModal, setClubListsModal] = React.useState<ClubListsModalState>({ isOpen: false });

  const [confirmConflictsModal, setConfirmConflictsModal] = React.useState<ConfirmConflictsModalState>({
    isOpen: false
  });

  const [activationPreviewModal, setActivationPreviewModal] = React.useState<ActivationPreviewModalState>({
    isOpen: false
  });

  const topScrollRef = React.useRef<HTMLDivElement | null>(null);
  const boardScrollRef = React.useRef<HTMLDivElement | null>(null);
  const [topScrollWidth, setTopScrollWidth] = React.useState(0);
  const boardScrollLeftRef = React.useRef(0);
  const boardWrapperRef = React.useRef<HTMLDivElement | null>(null);

  const restoreScrollRef = React.useRef<{
    windowScrollY: number;
    boardScrollLeft: number;
  } | null>(null);

  const [isHelpOpen, setIsHelpOpen] = React.useState(false);

  const hasLoadedRef = React.useRef(false);

  const rememberScrollPosition = React.useCallback(() => {
    restoreScrollRef.current = {
      windowScrollY: window.scrollY || window.pageYOffset || 0,
      boardScrollLeft: boardScrollRef.current?.scrollLeft || 0
    };
  }, []);

  const syncScrollFromTop = React.useCallback(() => {
    if (!topScrollRef.current || !boardScrollRef.current) return;
    boardScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
  }, []);

  const syncScrollFromBoard = React.useCallback(() => {
    if (!topScrollRef.current || !boardScrollRef.current) return;

    const nextScrollLeft = boardScrollRef.current.scrollLeft;
    boardScrollLeftRef.current = nextScrollLeft;
    topScrollRef.current.scrollLeft = nextScrollLeft;
  }, []);

  const load = React.useCallback(
    async (options?: { silent?: boolean }) => {
      try {
        setError(null);

        if (!hasLoadedRef.current) {
          setIsLoading(true);
        } else if (!options?.silent) {
          setIsReloading(true);
        }

        const statuses: string[] = ['ACCEPTED'];
        if (includeDrafts) statuses.push('PROGRAMME_DRAFT');
        if (includeWaitingList) statuses.push('WAITING_LIST', 'WAITING_LIST_CAN_BOOK', 'WAITING_LIST_NOT_BOOKED');

        const res = await getClubProgrammeBookings(user, programmeId, {
          statuses,
          includeDrafts
        });

        setData(res);
        hasLoadedRef.current = true;

        const days: ClubProgrammeBookingDay[] = Array.isArray(res?.days) ? res.days : [];

        setActiveDay(prevActiveDay => {
          const hasPrevDay = days.some(d => String(d.day) === String(prevActiveDay));
          if (hasPrevDay) return prevActiveDay;

          const firstNonEmpty = days
            .slice()
            .sort((a, b) => (weekdayOrder[normDay(a.day)] || 99) - (weekdayOrder[normDay(b.day)] || 99))
            .find(d => {
              const clubs: ClubProgrammeBookingClub[] = Array.isArray(d?.clubs) ? d.clubs : [];
              return clubs.some(c => Array.isArray(c?.items) && c.items.length > 0);
            });

          return firstNonEmpty?.day || 'MON';
        });
      } catch (e) {
        console.error('[ClubProgrammeBookingsBoard] load error', e);
        setError(
          String(propz.get(e, ['response', 'data', 'error'], propz.get(e, ['message'], 'Failed to load bookings')))
        );
        setData(null);
      } finally {
        setIsLoading(false);
        setIsReloading(false);
      }
    },
    [user, programmeId, includeDrafts, includeWaitingList]
  );

  const runItemAction = React.useCallback(
    async (messageId: string, action: 'SELECT_CLUB' | 'SELECT_WAITING_LIST' | 'UNSELECT' | 'REMOVE') => {
      const key = `${action}:${String(messageId)}`;

      try {
        setError(null);
        setActionKey(key);
        rememberScrollPosition();

        if (action === 'SELECT_CLUB') {
          await selectClubProgrammeBooking(user, programmeId, messageId, 'CLUB');
        } else if (action === 'SELECT_WAITING_LIST') {
          await selectClubProgrammeBooking(user, programmeId, messageId, 'WAITING_LIST');
        } else if (action === 'REMOVE') {
          await removeClubProgrammeBooking(user, programmeId, messageId);
        } else {
          await unselectClubProgrammeBooking(user, programmeId, messageId);
        }

        await load({ silent: true });
      } catch (e) {
        console.error('[ClubProgrammeBookingsBoard] action error', e);
        setError(
          String(
            propz.get(
              e,
              ['response', 'data', 'error'],
              propz.get(e, ['response', 'data', 'message'], propz.get(e, ['message'], 'Action failed'))
            )
          )
        );
      } finally {
        setActionKey(null);
      }
    },
    [user, programmeId, load]
  );

  const programmeStatus = String(propz.get(programme, ['status'], '') || '').toUpperCase();
  const programmeIsCompleted = !!propz.get(programme, ['completion', 'isCompleted'], false);
  const allocationActionsLocked = programmeIsCompleted;

  const getBookingActionButtonClass = React.useCallback((variant: string) => {
    if (variant === 'btn-primary') return 'pbCardActionButton pbCardActionButton--primary';
    if (variant === 'btn-outline-warning') return 'pbCardActionButton pbCardActionButton--warning';
    if (variant === 'btn-outline-danger') return 'pbCardActionButton pbCardActionButton--danger';
    return 'pbCardActionButton pbCardActionButton--muted';
  }, []);

  const renderItemActionButtons = React.useCallback(
    (it: ClubProgrammeBookingItem) => {
      const primaryAction = getPrimaryActionForItem(it);
      const secondaryAction = getSecondaryActionForItem(it);

      const lockedTitle = 'Allocation actions are locked because this programme is completed';

      return (
        <>
          <button
            type="button"
            className={getBookingActionButtonClass(primaryAction.variant)}
            disabled={
              allocationActionsLocked || primaryAction.disabled || actionKey !== null || primaryAction.action === null
            }
            onClick={() => {
              if (!primaryAction.action || allocationActionsLocked) return;
              runItemAction(String(it.messageId), primaryAction.action);
            }}
            title={allocationActionsLocked ? lockedTitle : primaryAction.title}
          >
            {primaryAction.action
              ? getActionButtonText(primaryAction.action, String(it.messageId), primaryAction.label, actionKey)
              : primaryAction.label}
          </button>

          {secondaryAction ? (
            <button
              type="button"
              className={getBookingActionButtonClass(secondaryAction.variant)}
              disabled={
                allocationActionsLocked ||
                secondaryAction.disabled ||
                actionKey !== null ||
                secondaryAction.action === null
              }
              onClick={() => {
                if (!secondaryAction.action || allocationActionsLocked) return;
                runItemAction(String(it.messageId), secondaryAction.action);
              }}
              title={allocationActionsLocked ? lockedTitle : secondaryAction.title}
            >
              {getActionButtonText(secondaryAction.action, String(it.messageId), secondaryAction.label, actionKey)}
            </button>
          ) : null}
        </>
      );
    },
    [actionKey, allocationActionsLocked, getBookingActionButtonClass, runItemAction]
  );

  const submitClubConfirm = React.useCallback(
    async (clubId: string, resolutions?: Array<{ permissionId: string; keepMessageId: string }>) => {
      return confirmClubProgrammeBookingsForClub(user, programmeId, clubId, resolutions || []);
    },
    [user, programmeId]
  );

  const submitDayConfirm = React.useCallback(
    async (selectionDayKey: string, resolutions?: Array<{ permissionId: string; keepMessageId: string }>) => {
      return confirmClubProgrammeBookingsForDay(user, programmeId, selectionDayKey, resolutions || []);
    },
    [user, programmeId]
  );

  const submitProgrammeConfirm = React.useCallback(
    async (resolutions?: Array<{ dayKey: string; permissionId: string; keepMessageId: string }>) => {
      return confirmClubProgrammeBookingsForProgramme(user, programmeId, resolutions || []);
    },
    [user, programmeId]
  );

  const runClubConfirmAction = React.useCallback(
    async (clubId: string, resolutions?: Array<{ permissionId: string; keepMessageId: string }>) => {
      const key = `CONFIRM_CLUB:${String(clubId)}`;

      try {
        setError(null);
        setActionKey(key);
        rememberScrollPosition();

        const response = await submitClubConfirm(clubId, resolutions);

        const responseCode = String(propz.get(response, ['code'], '') || propz.get(response, ['error'], ''));

        if (responseCode === 'CONFIRMATION_CONFLICTS_REQUIRE_RESOLUTION') {
          const rawConflicts = propz.get(response, ['conflicts'], []);
          const conflicts = Array.isArray(rawConflicts) ? rawConflicts : [];

          setConfirmConflictsModal({
            isOpen: true,
            mode: 'club',
            clubId: String(clubId),
            conflicts,
            isSubmitting: false
          });

          setError(null);
          return;
        }

        setConfirmConflictsModal({ isOpen: false });
        await load({ silent: true });
      } catch (e) {
        console.error('[ClubProgrammeBookingsBoard] confirm club error', e);

        const errorCode = String(
          propz.get(e, ['response', 'data', 'code'], '') || propz.get(e, ['response', 'data', 'error'], '')
        );

        if (errorCode === 'CONFIRMATION_CONFLICTS_REQUIRE_RESOLUTION') {
          const rawConflicts = propz.get(e, ['response', 'data', 'conflicts'], []);
          const conflicts = Array.isArray(rawConflicts) ? rawConflicts : [];

          setConfirmConflictsModal({
            isOpen: true,
            mode: 'club',
            clubId: String(clubId),
            conflicts,
            isSubmitting: false
          });

          setError(null);
          return;
        }

        setError(
          String(
            propz.get(
              e,
              ['response', 'data', 'error'],
              propz.get(e, ['response', 'data', 'message'], propz.get(e, ['message'], 'Confirm club failed'))
            )
          )
        );
      } finally {
        setActionKey(null);
      }
    },
    [submitClubConfirm, load, rememberScrollPosition]
  );

  const runClubBulkSelectAction = React.useCallback(
    async (clubId: string, mode: 'ALL' | 'FIRST_N', restToWaitingList?: boolean) => {
      const key = `BULK_SELECT_CLUB:${String(clubId)}:${mode}:${restToWaitingList ? 'WL' : 'NO_WL'}`;

      try {
        setError(null);
        setActionKey(key);
        rememberScrollPosition();

        await bulkSelectClubProgrammeBookingsForClub(user, programmeId, {
          clubId,
          mode,
          restToWaitingList: !!restToWaitingList
        });

        await load({ silent: true });
      } catch (e) {
        console.error('[ClubProgrammeBookingsBoard] bulk select club error', e);
        setError(
          String(
            propz.get(
              e,
              ['response', 'data', 'error'],
              propz.get(e, ['response', 'data', 'message'], propz.get(e, ['message'], 'Bulk selection failed'))
            )
          )
        );
      } finally {
        setActionKey(null);
      }
    },
    [user, programmeId, load, rememberScrollPosition]
  );

  const runDayBulkSelectAction = React.useCallback(
    async (selectionDayKey: string, mode: 'ALL' | 'FIRST_N', restToWaitingList?: boolean) => {
      const key = `BULK_SELECT_DAY:${String(selectionDayKey)}:${mode}:${restToWaitingList ? 'WL' : 'NO_WL'}`;

      try {
        setError(null);
        setActionKey(key);
        rememberScrollPosition();

        await bulkSelectClubProgrammeBookingsForDay(user, programmeId, {
          selectionDayKey,
          mode,
          restToWaitingList: !!restToWaitingList
        });

        await load({ silent: true });
      } catch (e) {
        console.error('[ClubProgrammeBookingsBoard] bulk select day error', e);
        setError(
          String(
            propz.get(
              e,
              ['response', 'data', 'error'],
              propz.get(e, ['response', 'data', 'message'], propz.get(e, ['message'], 'Bulk day selection failed'))
            )
          )
        );
      } finally {
        setActionKey(null);
      }
    },
    [user, programmeId, load, rememberScrollPosition]
  );

  const runProgrammeBulkSelectAction = React.useCallback(
    async (mode: 'ALL' | 'FIRST_N', restToWaitingList?: boolean) => {
      const key = `BULK_SELECT_PROGRAMME:${mode}:${restToWaitingList ? 'WL' : 'NO_WL'}`;

      try {
        setError(null);
        setActionKey(key);
        rememberScrollPosition();

        await bulkSelectClubProgrammeBookingsForProgramme(user, programmeId, {
          mode,
          restToWaitingList: !!restToWaitingList
        });

        await load({ silent: true });
      } catch (e) {
        console.error('[ClubProgrammeBookingsBoard] bulk select programme error', e);
        setError(
          String(
            propz.get(
              e,
              ['response', 'data', 'error'],
              propz.get(
                e,
                ['response', 'data', 'message'],
                propz.get(e, ['message'], 'Programme bulk selection failed')
              )
            )
          )
        );
      } finally {
        setActionKey(null);
      }
    },
    [user, programmeId, load, rememberScrollPosition]
  );

  const runDayConfirmAction = React.useCallback(
    async (selectionDayKey: string, resolutions?: Array<{ permissionId: string; keepMessageId: string }>) => {
      const key = `CONFIRM_DAY:${String(selectionDayKey)}`;

      try {
        setError(null);
        setActionKey(key);
        rememberScrollPosition();

        const response = await submitDayConfirm(selectionDayKey, resolutions);

        const responseCode = String(propz.get(response, ['code'], '') || propz.get(response, ['error'], ''));

        if (responseCode === 'CONFIRMATION_CONFLICTS_REQUIRE_RESOLUTION') {
          const rawConflicts = propz.get(response, ['conflicts'], []);
          const conflicts = Array.isArray(rawConflicts) ? rawConflicts : [];

          setConfirmConflictsModal({
            isOpen: true,
            mode: 'day',
            selectionDayKey: String(selectionDayKey),
            conflicts,
            isSubmitting: false
          });

          setError(null);
          return;
        }

        setConfirmConflictsModal({ isOpen: false });
        await load({ silent: true });
      } catch (e) {
        console.error('[ClubProgrammeBookingsBoard] confirm day error', e);

        const errorCode = String(
          propz.get(e, ['response', 'data', 'code'], '') || propz.get(e, ['response', 'data', 'error'], '')
        );

        if (errorCode === 'CONFIRMATION_CONFLICTS_REQUIRE_RESOLUTION') {
          const rawConflicts = propz.get(e, ['response', 'data', 'conflicts'], []);
          const conflicts = Array.isArray(rawConflicts) ? rawConflicts : [];

          setConfirmConflictsModal({
            isOpen: true,
            mode: 'day',
            selectionDayKey: String(selectionDayKey),
            conflicts,
            isSubmitting: false
          });

          setError(null);
          return;
        }

        setError(
          String(
            propz.get(
              e,
              ['response', 'data', 'error'],
              propz.get(e, ['response', 'data', 'message'], propz.get(e, ['message'], 'Confirm day failed'))
            )
          )
        );
      } finally {
        setActionKey(null);
      }
    },
    [submitDayConfirm, load, rememberScrollPosition]
  );

  const runProgrammeConfirmAction = React.useCallback(
    async (resolutions?: Array<{ dayKey: string; permissionId: string; keepMessageId: string }>) => {
      const key = 'CONFIRM_PROGRAMME';

      try {
        setError(null);
        setActionKey(key);
        rememberScrollPosition();

        const response = await submitProgrammeConfirm(resolutions);

        const responseCode = String(propz.get(response, ['code'], '') || propz.get(response, ['error'], ''));

        if (responseCode === 'CONFIRMATION_CONFLICTS_REQUIRE_RESOLUTION') {
          const rawConflicts = propz.get(response, ['conflicts'], []);
          const conflicts = Array.isArray(rawConflicts) ? rawConflicts : [];

          setConfirmConflictsModal({
            isOpen: true,
            mode: 'programme',
            conflicts,
            isSubmitting: false
          });

          setError(null);
          return;
        }

        setConfirmConflictsModal({ isOpen: false });
        await load({ silent: true });
      } catch (e) {
        console.error('[ClubProgrammeBookingsBoard] confirm programme error', e);

        const errorCode = String(
          propz.get(e, ['response', 'data', 'code'], '') || propz.get(e, ['response', 'data', 'error'], '')
        );

        if (errorCode === 'CONFIRMATION_CONFLICTS_REQUIRE_RESOLUTION') {
          const rawConflicts = propz.get(e, ['response', 'data', 'conflicts'], []);
          const conflicts = Array.isArray(rawConflicts) ? rawConflicts : [];

          setConfirmConflictsModal({
            isOpen: true,
            mode: 'programme',
            conflicts,
            isSubmitting: false
          });

          setError(null);
          return;
        }

        setError(
          String(
            propz.get(
              e,
              ['response', 'data', 'error'],
              propz.get(e, ['response', 'data', 'message'], propz.get(e, ['message'], 'Confirm programme failed'))
            )
          )
        );
      } finally {
        setActionKey(null);
      }
    },
    [submitProgrammeConfirm, load, rememberScrollPosition]
  );

  const openActivationPreview = React.useCallback(async () => {
    try {
      setError(null);

      setActivationPreviewModal({
        isOpen: true,
        isLoading: true,
        isActivating: false,
        data: null
      });

      const preview = await getClubProgrammeActivationPreview(user, programmeId);

      setActivationPreviewModal({
        isOpen: true,
        isLoading: false,
        isActivating: false,
        data: preview
      });
    } catch (e) {
      console.error('[ClubProgrammeBookingsBoard] activation preview error', e);

      setActivationPreviewModal({ isOpen: false });

      setError(
        String(
          propz.get(
            e,
            ['response', 'data', 'error'],
            propz.get(
              e,
              ['response', 'data', 'message'],
              propz.get(e, ['message'], 'Failed to load activation preview')
            )
          )
        )
      );
    }
  }, [user, programmeId]);

  const runBatchActivateProgrammeClubs = React.useCallback(async () => {
    try {
      setError(null);

      setActivationPreviewModal(prev =>
        prev.isOpen
          ? {
              ...prev,
              isActivating: true
            }
          : prev
      );

      await batchActivateClubProgrammeClubs(user, programmeId, {
        timeZoneName: 'utc'
      });

      setActivationPreviewModal({ isOpen: false });

      await load({ silent: true });
      window.location.reload();
    } catch (e) {
      console.error('[ClubProgrammeBookingsBoard] batch activate clubs error', e);

      setActivationPreviewModal(prev =>
        prev.isOpen
          ? {
              ...prev,
              isActivating: false
            }
          : prev
      );

      setError(
        String(
          propz.get(
            e,
            ['response', 'data', 'error'],
            propz.get(
              e,
              ['response', 'data', 'message'],
              propz.get(e, ['message'], 'Failed to activate programme clubs')
            )
          )
        )
      );
    }
  }, [user, programmeId, load]);

  const submitClubConflictResolutions = React.useCallback(
    async (resolutions: Array<{ permissionId: string; keepMessageId: string }>) => {
      if (!confirmConflictsModal.isOpen || confirmConflictsModal.mode !== 'club') return;

      const clubId = String(confirmConflictsModal.clubId || '');
      if (!clubId) return;

      try {
        setConfirmConflictsModal(prev =>
          prev.isOpen
            ? {
                ...prev,
                isSubmitting: true
              }
            : prev
        );

        await runClubConfirmAction(clubId, resolutions);
      } finally {
        setConfirmConflictsModal(prev =>
          prev.isOpen
            ? {
                ...prev,
                isSubmitting: false
              }
            : prev
        );
      }
    },
    [confirmConflictsModal, runClubConfirmAction]
  );

  const submitConfirmConflictResolutions = React.useCallback(
    async (
      resolutions:
        | Array<{ permissionId: string; keepMessageId: string }>
        | Array<{ dayKey: string; permissionId: string; keepMessageId: string }>
    ) => {
      if (!confirmConflictsModal.isOpen) return;

      try {
        setConfirmConflictsModal(prev =>
          prev.isOpen
            ? {
                ...prev,
                isSubmitting: true
              }
            : prev
        );

        if (confirmConflictsModal.mode === 'club') {
          const clubId = String(confirmConflictsModal.clubId || '');
          if (!clubId) return;

          await runClubConfirmAction(clubId, resolutions as Array<{ permissionId: string; keepMessageId: string }>);
          return;
        }

        if (confirmConflictsModal.mode === 'day') {
          const selectionDayKey = String(confirmConflictsModal.selectionDayKey || '');
          if (!selectionDayKey) return;

          await runDayConfirmAction(
            selectionDayKey,
            resolutions as Array<{ permissionId: string; keepMessageId: string }>
          );
          return;
        }

        await runProgrammeConfirmAction(
          resolutions as Array<{ dayKey: string; permissionId: string; keepMessageId: string }>
        );
      } finally {
        setConfirmConflictsModal(prev =>
          prev.isOpen
            ? {
                ...prev,
                isSubmitting: false
              }
            : prev
        );
      }
    },
    [confirmConflictsModal, runClubConfirmAction, runDayConfirmAction, runProgrammeConfirmAction]
  );

  React.useEffect(() => {
    if (!programmeId) return;
    load();
  }, [programmeId, load]);

  React.useLayoutEffect(() => {
    let raf = 0;

    const syncTopInnerWidth = () => {
      if (!boardScrollRef.current) return;

      const scrollWidth = boardScrollRef.current.scrollWidth || 0;
      setTopScrollWidth(scrollWidth);
    };

    raf = window.requestAnimationFrame(syncTopInnerWidth);
    window.addEventListener('resize', syncTopInnerWidth);

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', syncTopInnerWidth);
    };
  }, [activeDay, data]);

  React.useLayoutEffect(() => {
    if (boardScrollRef.current) {
      boardScrollRef.current.scrollLeft = boardScrollLeftRef.current;
    }

    if (topScrollRef.current) {
      topScrollRef.current.scrollLeft = boardScrollLeftRef.current;
    }
  }, [data, activeDay]);

  React.useLayoutEffect(() => {
    if (!restoreScrollRef.current) return;

    const { windowScrollY, boardScrollLeft } = restoreScrollRef.current;

    if (boardScrollRef.current) {
      boardScrollRef.current.scrollLeft = boardScrollLeft;
    }

    if (topScrollRef.current) {
      topScrollRef.current.scrollLeft = boardScrollLeft;
    }

    window.scrollTo({
      top: windowScrollY,
      left: 0,
      behavior: 'auto'
    });

    const raf = window.requestAnimationFrame(() => {
      if (boardScrollRef.current) {
        boardScrollRef.current.scrollLeft = boardScrollLeft;
      }
      if (topScrollRef.current) {
        topScrollRef.current.scrollLeft = boardScrollLeft;
      }

      window.scrollTo({
        top: windowScrollY,
        left: 0,
        behavior: 'auto'
      });

      restoreScrollRef.current = null;
    });

    return () => window.cancelAnimationFrame(raf);
  }, [data]);

  const days: ClubProgrammeBookingDay[] = Array.isArray(data?.days) ? data.days : [];
  const conflicts: ClubProgrammeBookingConflict[] = Array.isArray(data?.conflicts) ? data.conflicts : [];
  const studentConflicts: ClubProgrammeStudentConflict[] = Array.isArray(data?.studentConflicts)
    ? data.studentConflicts
    : [];

  const sortedDays = days
    .slice()
    .sort((a, b) => (weekdayOrder[normDay(a.day)] || 99) - (weekdayOrder[normDay(b.day)] || 99));

  const active: ClubProgrammeBookingDay | undefined = sortedDays.find(d => d.day === activeDay) || sortedDays[0];

  const activeClubs: ClubProgrammeBookingClub[] = Array.isArray(active?.clubs) ? active.clubs : [];

  const selectionMode = String(data?.selectionRules?.mode || 'SINGLE').toUpperCase();
  const isPriorityMode = selectionMode === 'PRIORITY';

  const programmeName = String(programme?.name || 'this programme');

  const programmeTypeLabel = isPriorityMode ? 'Priority' : 'Single';

  React.useEffect(() => {
    if (typeof onSelectionModeChange === 'function') {
      onSelectionModeChange(selectionMode);
    }
  }, [selectionMode, onSelectionModeChange]);

  React.useEffect(() => registerPageHelp(() => setIsHelpOpen(true), `How bookings work: ${programmeName}`), [
    programmeName
  ]);

  const helpIntroText = isPriorityMode
    ? `You are currently reviewing allocation outcomes for programme "${programmeName}". This programme uses Priority booking mode. Quick allocation actions only affect Priority 1 bookings, so lower priorities stay untouched until you review them manually.`
    : `You are currently reviewing allocation outcomes for programme "${programmeName}". This programme uses Single booking mode. Quick allocation actions affect all eligible booked pupils for each club.`;

  const priorityModeHint = isPriorityMode
    ? 'Priority mode: only Priority 1 bookings are affected.'
    : 'Single mode: all booked pupils are affected.';

  const dayBulkAllLabel = 'Select all';
  const dayBulkFirstNLabel = 'Select first N';
  const dayBulkFirstNWithWlLabel = 'Select first N + WL';

  const programmeBulkAllLabel = 'Select all';
  const programmeBulkFirstNLabel = 'Select first N';
  const programmeBulkFirstNWithWlLabel = 'Select first N + WL';

  const activeDayPendingCount = activeClubs.reduce((acc, club) => {
    const items: ClubProgrammeBookingItem[] = Array.isArray(club?.items) ? club.items : [];

    const pendingRemoveCount = items.filter(it => !!it.uiState?.isPendingRemove).length;

    return (
      acc +
      Number(propz.get(club, ['selectedClubCount'], 0) || 0) +
      Number(propz.get(club, ['selectedWaitingListCount'], 0) || 0) +
      pendingRemoveCount
    );
  }, 0);

  const programmePendingCount = sortedDays.reduce((dayAcc, day) => {
    const clubs: ClubProgrammeBookingClub[] = Array.isArray(day?.clubs) ? day.clubs : [];

    const dayPending = clubs.reduce((clubAcc, club) => {
      const items: ClubProgrammeBookingItem[] = Array.isArray(club?.items) ? club.items : [];

      const pendingRemoveCount = items.filter(it => !!it.uiState?.isPendingRemove).length;

      return (
        clubAcc +
        Number(propz.get(club, ['selectedClubCount'], 0) || 0) +
        Number(propz.get(club, ['selectedWaitingListCount'], 0) || 0) +
        pendingRemoveCount
      );
    }, 0);

    return dayAcc + dayPending;
  }, 0);

  const canOpenActivationPreview =
    programmeStatus === 'ACTIVE' && !programmeIsCompleted && actionKey === null && programmePendingCount === 0;
  const dayConflicts: ClubProgrammeBookingConflict[] = conflicts.filter(c => c.day === active?.day);

  React.useEffect(() => {
    if (typeof onActivationAvailabilityChange === 'function') {
      onActivationAvailabilityChange(canOpenActivationPreview);
    }
  }, [canOpenActivationPreview, onActivationAvailabilityChange]);

  const lastHandledActivationRequestRef = React.useRef<number>(0);

  React.useEffect(() => {
    const token = Number(activationRequestToken || 0);

    if (!token) return;
    if (token === lastHandledActivationRequestRef.current) return;

    lastHandledActivationRequestRef.current = token;

    if (canOpenActivationPreview) {
      openActivationPreview();
    }
  }, [activationRequestToken, canOpenActivationPreview, openActivationPreview]);

  const activeStudentConflicts: ClubProgrammeStudentConflict[] = studentConflicts.filter(c => c.day === active?.day);

  const dayHasConflicts = activeStudentConflicts.length > 0 || dayConflicts.length > 0;

  if (!programmeId) return <div className="alert alert-light border">Programme is missing.</div>;
  if (isLoading) return <Loader />;

  const liveConflict = conflictModal.isOpen
    ? activeStudentConflicts.find(
        c =>
          String(c.permissionId || '') === String(conflictModal.conflict.permissionId || '') &&
          String(c.day || '') === String(conflictModal.conflict.day || '')
      ) || conflictModal.conflict
    : null;

  return (
    <div className="pbBoardPage" ref={boardWrapperRef}>
      {error ? <div className="pbErrorBanner">{error}</div> : null}

      <div className="pbControlBar">
        <div className="pbControlBarInner">
          <div className="cpBookingsPendingGroup">
            <span className="cpBookingsPendingBadge">
              Day pending: <b>{activeDayPendingCount}</b>
            </span>
            <span className="cpBookingsPendingBadge cpBookingsPendingBadge--strong">
              Programme pending: <b>{programmePendingCount}</b>
            </span>
          </div>
          {isReloading ? <span className="pbBoardUpdating">Updating…</span> : null}
          {active ? (
            <>
              <div className="pbControlDivider" />
              <div className="pbControlLabel">Day</div>
              <BulkActionMenu
                label="Bulk select"
                disabled={actionKey !== null || !active || allocationActionsLocked}
                items={[
                  {
                    key: 'day-all',
                    label: dayBulkAllLabel,
                    onClick: () => runDayBulkSelectAction(String(active.day), 'ALL', false)
                  },
                  {
                    key: 'day-first-n',
                    label: dayBulkFirstNLabel,
                    onClick: () => runDayBulkSelectAction(String(active.day), 'FIRST_N', false)
                  },
                  {
                    key: 'day-first-n-wl',
                    label: dayBulkFirstNWithWlLabel,
                    onClick: () => runDayBulkSelectAction(String(active.day), 'FIRST_N', true)
                  }
                ]}
              />
              <button
                type="button"
                className="pbConfirmDayButton"
                disabled={actionKey !== null || !active || activeDayPendingCount === 0 || allocationActionsLocked}
                onClick={() => {
                  if (!active) return;
                  runDayConfirmAction(String(active.day));
                }}
              >
                <span aria-hidden="true">OK</span>
                {active && actionKey === `CONFIRM_DAY:${String(active.day)}` ? 'Saving...' : 'Confirm day'}
              </button>
            </>
          ) : null}
          <div className="pbControlDivider" />
          <div className="pbControlLabel">Programme</div>
          <BulkActionMenu
            label="Bulk select"
            disabled={actionKey !== null || sortedDays.length === 0 || allocationActionsLocked}
            items={[
              {
                key: 'programme-all',
                label: programmeBulkAllLabel,
                onClick: () => runProgrammeBulkSelectAction('ALL', false)
              },
              {
                key: 'programme-first-n',
                label: programmeBulkFirstNLabel,
                onClick: () => runProgrammeBulkSelectAction('FIRST_N', false)
              },
              {
                key: 'programme-first-n-wl',
                label: programmeBulkFirstNWithWlLabel,
                onClick: () => runProgrammeBulkSelectAction('FIRST_N', true)
              }
            ]}
          />
          <button
            type="button"
            className="pbConfirmProgrammeButton"
            disabled={actionKey !== null || programmePendingCount === 0 || allocationActionsLocked}
            onClick={() => runProgrammeConfirmAction()}
          >
            <span aria-hidden="true">OK</span>
            {actionKey === 'CONFIRM_PROGRAMME' ? 'Saving...' : 'Confirm programme allocations'}
          </button>
          <div className="pbControlHint">{priorityModeHint}</div>
        </div>
      </div>

      <div className="pbDayTabs" role="tablist">
        {sortedDays.map(d => {
          const day = String(d.day);
          const label = String(d.dayLabel || d.day);
          const clubs = (propz.get(d, ['clubs'], []) || []) as any[];
          const count = clubs.reduce((acc, c) => acc + ((c.items || []).length || 0), 0);
          const isActive = day === String(activeDay);
          const hasDayConflicts = conflicts.some(c => String(c.day) === day);
          return (
            <button
              key={day}
              type="button"
              className={`pbDayTab ${isActive ? 'pbDayTab--active' : ''}`}
              onClick={() => setActiveDay(day)}
              title={hasDayConflicts ? 'Conflicts detected on this day' : undefined}
            >
              <span>{label}</span>
              <span className="pbDayTabCount">{count}</span>
              {hasDayConflicts ? <span className="pbDayTabConflict">!</span> : null}
            </button>
          );
        })}
      </div>

      <div className="pbInfoBanner">
        <span className="pbInfoIcon" aria-hidden="true">
          i
        </span>
        <span>
          This board processes and confirms <b>programme allocations</b>. To adjust live club membership afterwards, use
          the normal <b>Clubs</b> interface.
        </span>
      </div>
      {allocationActionsLocked ? (
        <div className="pbLockedBanner">
          This programme is completed. Allocation actions are now locked here. If you need to adjust live club
          membership, please use the normal <b>Clubs</b> interface and the relevant <b>club events</b>.
        </div>
      ) : null}
      {!active ? (
        <div className="pbEmptyState">No data</div>
      ) : activeClubs.length === 0 ? (
        <div className="pbEmptyState">No clubs for this day.</div>
      ) : (
        <>
          <div ref={topScrollRef} onScroll={syncScrollFromTop} className="pbTopScroll pb-scroll">
            <div style={{ width: topScrollWidth || 1, height: 1 }} />
          </div>
          <div ref={boardScrollRef} onScroll={syncScrollFromBoard} className="pbBoardScroll pb-scroll">
            <div className="pbBoardColumns">
              {activeClubs.map(c => (
                <ClubProgrammeBookingClubColumn
                  key={String(propz.get(c, ['clubId']))}
                  club={c}
                  activeDayLabel={String(active?.dayLabel || active?.day || '')}
                  isPriorityMode={isPriorityMode}
                  actionKey={actionKey}
                  activeStudentConflicts={activeStudentConflicts}
                  renderItemActionButtons={renderItemActionButtons}
                  onOpenAnswers={(title, item) => setAnswerModal({ isOpen: true, title, item })}
                  onOpenConflict={conflict => setConflictModal({ isOpen: true, conflict })}
                  onOpenClubLists={(clubId, clubName) => setClubListsModal({ isOpen: true, clubId, clubName })}
                  onRunClubBulkSelectAction={runClubBulkSelectAction}
                  onRunClubConfirmAction={runClubConfirmAction}
                  allocationActionsLocked={allocationActionsLocked}
                />
              ))}
            </div>
          </div>
        </>
      )}

      <ClubProgrammeBookingConflictModal
        isOpen={conflictModal.isOpen}
        conflict={liveConflict}
        renderItemActionButtons={renderItemActionButtons}
        onClose={() => setConflictModal({ isOpen: false })}
      />

      <ClubProgrammeClubListsModal
        isOpen={clubListsModal.isOpen}
        clubId={clubListsModal.isOpen ? clubListsModal.clubId : ''}
        clubName={clubListsModal.isOpen ? clubListsModal.clubName : ''}
        club={
          clubListsModal.isOpen
            ? activeClubs.find(c => String(propz.get(c, ['clubId'], '')) === String(clubListsModal.clubId || '')) ||
              null
            : null
        }
        actionKey={actionKey}
        onClose={() => setClubListsModal({ isOpen: false })}
        onConfirmClub={clubId => runClubConfirmAction(clubId)}
      />

      <ClubProgrammeActivationPreviewModal
        isOpen={activationPreviewModal.isOpen}
        isLoading={activationPreviewModal.isOpen ? !!activationPreviewModal.isLoading : false}
        isActivating={activationPreviewModal.isOpen ? !!activationPreviewModal.isActivating : false}
        data={activationPreviewModal.isOpen ? activationPreviewModal.data : null}
        onClose={() => {
          if (activationPreviewModal.isOpen && activationPreviewModal.isActivating) return;
          setActivationPreviewModal({ isOpen: false });
        }}
        onActivate={() => runBatchActivateProgrammeClubs()}
      />

      {isHelpOpen ? (
        <HelpModal
          isOpen
          title="How programme allocations work"
          badgeText={`${programmeTypeLabel} mode`}
          subtitle={helpIntroText}
          onClose={() => setIsHelpOpen(false)}
        >
          <div className="p-2">
            <div
              className="border rounded p-3 mb-3"
              style={{ background: 'linear-gradient(135deg, #f5fbff 0%, #eef9f1 100%)' }}
            >
              <div className="h5 mb-2">Quick overview</div>
              <div className="mb-2">
                <span className="badge badge-info mr-2">{programmeTypeLabel} mode</span>
                <span className="badge badge-light border">{programmeName}</span>
              </div>
              <div className="text-muted">{helpIntroText}</div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <div className="border rounded p-3 h-100">
                  <div className="font-weight-bold mb-2">Top counters</div>
                  <div className="mb-2">
                    <span className="badge badge-light border mr-2">Day pending</span>
                    Number of not-yet-confirmed selections for the currently open day.
                  </div>
                  <div>
                    <span className="badge badge-success mr-2">Programme pending</span>
                    Total number of not-yet-confirmed selections across the whole programme.
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <div className="border rounded p-3 h-100">
                  <div className="font-weight-bold mb-2">Actions menus</div>
                  <div className="mb-2">
                    <b>Day actions</b> apply quick allocation rules to all clubs on the selected day.
                  </div>
                  <div>
                    <b>Programme actions</b> apply quick allocation rules to all clubs in the programme.
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <div className="border rounded p-3 h-100">
                  <div className="font-weight-bold mb-2">Club card</div>
                  <div className="mb-2">Each club card shows:</div>
                  <ul className="mb-0 pl-3">
                    <li>places, filled and remaining spaces</li>
                    <li>waiting list count</li>
                    <li>pending club selections</li>
                    <li>pending waiting list selections</li>
                  </ul>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <div className="border rounded p-3 h-100">
                  <div className="font-weight-bold mb-2">Statuses</div>
                  <div className="mb-2">
                    <span className="badge badge-info mr-2">Selected: Club</span>
                    Chosen, but not yet confirmed.
                  </div>
                  <div className="mb-2">
                    <span className="badge badge-warning mr-2">Selected: WL</span>
                    Put into waiting list, but not yet confirmed.
                  </div>
                  <div className="mb-2">
                    <span className="badge badge-success mr-2">In club</span>
                    Confirmed club allocation.
                  </div>
                  <div>
                    <span className="badge badge-warning mr-2">In waiting list</span>
                    Confirmed waiting list allocation.
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <div className="border rounded p-3 h-100">
                  <div className="font-weight-bold mb-2">Student conflicts</div>

                  <div className="mb-2">
                    A conflict means that the same student appears in more than one club choice for the same day.
                  </div>

                  <div className="mb-2">
                    <span className="badge badge-danger mr-2">!</span>A red marker on a day tab shows that at least one
                    conflict exists on that day.
                  </div>

                  <div className="mb-2">
                    <span className="badge badge-light border mr-2">Conflict icon</span>
                    Use the warning icon on a pupil card to open conflict details and compare all choices for that
                    student.
                  </div>

                  <div>
                    Confirming one club does not automatically resolve every conflict for the rest of that day, so it is
                    worth reviewing conflicts before confirming day or programme allocations.
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <div className="border rounded p-3 h-100">
                  <div className="font-weight-bold mb-2">Quick allocation buttons</div>
                  <div className="mb-2">
                    <b>Select all</b> selects all eligible bookings into clubs.
                  </div>
                  <div className="mb-2">
                    <b>Select first N</b> fills club spaces up to the club limit.
                  </div>
                  <div>
                    <b>Select first N + WL</b> fills club spaces first, then puts the rest into waiting list.
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <div className="border rounded p-3 h-100">
                  <div className="font-weight-bold mb-2">After allocations are confirmed</div>
                  <div className="mb-2">
                    This board is for processing <b>programme booking allocations</b> and confirming those outcomes.
                  </div>
                  <div className="mb-2">
                    If you later need to add new pupils into clubs, remove pupils, or otherwise adjust the live
                    participant lists, please do that through the normal <b>Clubs</b> interface and the relevant{' '}
                    <b>club events</b> in the usual way.
                  </div>
                  <div>
                    In other words, use this page to finalise programme allocations, and use the standard club / event
                    tools for ongoing participant management afterwards.
                  </div>
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <div className="border rounded p-3 h-100">
                  <div className="font-weight-bold mb-2">{isPriorityMode ? 'Priority mode' : 'Single mode'}</div>

                  {isPriorityMode ? (
                    <div>
                      In <b>Priority mode</b>, quick allocation actions only affect <b>Priority 1</b> bookings. Lower
                      priorities remain unchanged until you review them manually or move them one by one.
                    </div>
                  ) : (
                    <div>
                      In <b>Single mode</b>, quick allocation actions affect all eligible booked pupils for the club.
                      There is no priority filtering when you use bulk allocation actions.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border rounded p-3 mt-2" style={{ background: '#fffdf3' }}>
              <div className="font-weight-bold mb-2">Typical allocation workflow</div>
              <ol className="mb-0 pl-3">
                <li>Choose a day.</li>
                <li>Use a quick allocation action for the day, programme, or an individual club.</li>
                <li>Review club cards, waiting lists and any pending removals.</li>
                <li>Check student conflicts before confirming outcomes.</li>
                <li>Open “View lists” if you want to inspect allocation details more closely.</li>
                <li>Confirm allocations at club, day, or programme level.</li>
              </ol>
            </div>
          </div>
        </HelpModal>
      ) : null}

      <ClubProgrammeBookingAnswersModal
        isOpen={answerModal.isOpen}
        title={answerModal.isOpen ? answerModal.title : ''}
        fields={answerModal.isOpen ? answerModal.item.fields || [] : []}
        formDefinition={answerModal.isOpen ? answerModal.item.formDefinition : undefined}
        formAnswers={answerModal.isOpen ? answerModal.item.formAnswers : undefined}
        onClose={() => setAnswerModal({ isOpen: false })}
      />

      <ClubProgrammeConfirmConflictsModal
        isOpen={confirmConflictsModal.isOpen}
        mode={confirmConflictsModal.isOpen ? confirmConflictsModal.mode : 'club'}
        title={
          confirmConflictsModal.isOpen
            ? confirmConflictsModal.mode === 'club'
              ? 'Resolve club confirmation conflicts'
              : confirmConflictsModal.mode === 'day'
              ? 'Resolve day confirmation conflicts'
              : 'Resolve programme confirmation conflicts'
            : 'Resolve allocation conflicts'
        }
        description={
          confirmConflictsModal.isOpen
            ? confirmConflictsModal.mode === 'club'
              ? 'Some students are already confirmed in another club on the same day. Choose which club should be kept for each student before confirming this club.'
              : confirmConflictsModal.mode === 'day'
              ? 'Some students have multiple club allocations for this day. Choose which club should be kept for each student before confirming day allocations.'
              : 'Some students have multiple club allocations in the programme. Choose which club should be kept for each student before confirming programme allocations.'
            : undefined
        }
        conflicts={confirmConflictsModal.isOpen ? confirmConflictsModal.conflicts : []}
        isSubmitting={confirmConflictsModal.isOpen ? confirmConflictsModal.isSubmitting : false}
        onClose={() => {
          if (confirmConflictsModal.isOpen && confirmConflictsModal.isSubmitting) return;
          setConfirmConflictsModal({ isOpen: false });
        }}
        onSubmit={submitConfirmConflictResolutions}
      />
    </div>
  );
};
